import React, { useMemo } from 'react'
import { intl } from 'lang/intl'
import * as Yup from 'yup'

import * as TtForms from '@23/react-forms'

import { type ActionCardResponse } from '../blocs/useActionCards'

import './ActionCardForm.scss'

const fieldSchema = (field: ActionCardResponse['fields'][number]) => {
  switch (field.type) {
    case 'check':
      return Yup.boolean()
    case 'text-short':
      return Yup.string()
        .required('Required')
        .max(100, 'Maximum 100 characters')
    case 'select':
      return Yup.string()
        .required(intl.formatMessage({ defaultMessage: 'Required' }))
        .oneOf(
          field.choices ?? [],
          intl.formatMessage({ defaultMessage: 'Select one of the options' })
        )
    default:
      return Yup.string()
        .required('Required')
        .max(4000, 'Maximum 4000 characters')
  }
}

interface ActionCardAudienceFormProps {
  card: ActionCardResponse
  onSubmit: (values: Record<string, string | boolean>) => void
}

const ActionCardForm = ({ card, onSubmit }: ActionCardAudienceFormProps) => {
  const initialValues = useMemo(() => {
    return card.fields.reduce<Record<string, string | boolean>>(
      (acc, field) => {
        acc[field.id] = field.type === 'check' ? false : ''
        return acc
      },
      {}
    )
  }, [card.fields])

  const validationSchema = Yup.object(
    Object.fromEntries(
      card.fields.map((field) => [field.id, fieldSchema(field)])
    )
  )

  type FormValues = Yup.InferType<typeof validationSchema>

  return (
    <TtForms.Form<FormValues>
      label={intl.formatMessage({ defaultMessage: 'Answer Action Card Form' })}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) =>
        onSubmit(values as Record<string, string | boolean>)
      }
      enableReinitialize
    >
      <div className="action-card-options">
        {card.fields.map((field) => {
          switch (field.type) {
            case 'check': {
              return (
                <TtForms.Checkbox
                  key={field.id}
                  className="action-card-checkbox"
                  name={`${field.id}`}
                  label={field.label}
                />
              )
            }
            case 'text-short': {
              return (
                <TtForms.TextField
                  key={field.id}
                  className="action-card-text-field"
                  name={`${field.id}`}
                  label={field.label}
                  hideLabel
                  placeholder={field.label}
                  size="medium"
                />
              )
            }
            case 'select': {
              return (
                <TtForms.Select
                  key={field.id}
                  className="action-card-select"
                  name={`${field.id}`}
                  label={field.label}
                  hoist
                  size="medium"
                >
                  {(field.choices ?? []).map((choice) => (
                    <TtForms.Option
                      key={choice}
                      name={`${field.id}`}
                      value={choice}
                    >
                      {choice}
                    </TtForms.Option>
                  ))}
                </TtForms.Select>
              )
            }
            default: {
              return (
                <TtForms.TextArea
                  key={field.id}
                  className="action-card-text-area"
                  name={`${field.id}`}
                  label={field.label}
                  hideLabel
                  placeholder={field.label}
                  lines={3}
                />
              )
            }
          }
        })}
      </div>

      <TtForms.SubmitButton
        className="action-card-submit-btn"
        size="medium"
        variant="default"
        pill
      >
        {card.submitLabel}
      </TtForms.SubmitButton>
    </TtForms.Form>
  )
}

export { ActionCardForm }
